import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, subMonths, addWeeks, subWeeks,
  isSameMonth, isSameDay, isToday, parseISO, addDays,
} from "date-fns";
import { es } from "date-fns/locale";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, ChevronRight, CalendarDays, List, Plus, Truck, MapPin, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import KanbanMontajes from "@/components/calendario/KanbanMontajes";
import FichaMontaje, { CambiarFechaDialog, ConfirmarCitaDialog } from "@/components/calendario/FichaMontaje";
import { DiaMontajes, MapaMontajes, ResumenConjunto } from "@/components/calendario/VistasMontajes";
import { Eye as EyeIcon, MapPin as MapPinIcon, Sparkles as SparklesIcon, ListChecks } from "lucide-react";
import { BarraSeleccion, PrepararRutaDialog } from "@/components/calendario/BarraSeleccion";
import { ESTADO_COLOR, PROVINCIA_COLOR, PROVINCIA_DEFAULT_COLOR, provinciaColor, RutaPill, MontajePill } from "@/components/calendario/Pills";
import { motivoNoElegible, snapshotMontaje, leerSeleccion, guardarSeleccion, leerCtx, guardarCtx, borrarCtx, KEY_REPARTO_SEL } from "@/components/calendario/seleccionRuta";
import { LeyendaMaterial } from "@/components/MaterialBadge";

// Filtro «Material» del calendario sobre `disponibilidad` (fuente única backend)
const pasaFiltroMaterial = (mo, f) => {
  const d = mo.disponibilidad || {};
  if (f === "todos") return true;
  if (f === "listo") return !!mo.material_listo;
  if (f === "no_listo") return !mo.material_listo;
  if (f === "completo") return d.estado === "completo" || (d.estado === "sin_material" && d.candidata);
  if (f === "parcial") return d.estado === "parcial";
  if (f === "almacen") return mo.material_listo && (d.ubicacion === "almacen" || d.ubicacion === "mixto");
  if (f === "pdv") return mo.material_listo && (d.ubicacion === "pdv" || d.ubicacion === "mixto");
  return true;
};
const VIEWS_SELECCION = ["semana", "dia", "mapa"];

const VIEWS = ["mes", "semana", "dia", "lista", "mapa"];
const VIEWS_RUTAS = ["mes", "semana", "lista"];
const MODOS = [
  { key: "rutas",    label: "Rutas",    icon: Truck },
  { key: "montajes", label: "Montajes", icon: MapPin },
];

export default function CalendarioRutas() {
  const nav = useNavigate();
  const location = useLocation();
  // Contexto guardado al ir a Reparto desde la selección (vista, fecha, filtros): se restaura una sola vez al volver
  const [ctx0] = useState(() => { const c = leerCtx(); if (c) borrarCtx(); return c; });
  const [modo, setModo] = useState(() => (ctx0 ? "montajes" : localStorage.getItem("dnf-cal-modo") || "rutas"));
  const [view, setView] = useState(() => ctx0?.view || localStorage.getItem("dnf-cal-view") || "mes");
  const [cursor, setCursor] = useState(() => (ctx0?.cursor ? parseISO(ctx0.cursor) : new Date()));
  const [rutas, setRutas] = useState([]);
  const [montajes, setMontajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [filtroProvincias, setFiltroProvincias] = useState(ctx0?.filtroProvincias || []);       // [] = todas
  const [mostrarRealizados, setMostrarRealizados] = useState(!!ctx0?.mostrarRealizados);
  const [sugerir, setSugerir] = useState(!!ctx0?.sugerir);
  const [sugeridos, setSugeridos] = useState([]);
  const [sugLoading, setSugLoading] = useState(false);
  const [dia, setDia] = useState(() => (ctx0?.dia ? parseISO(ctx0.dia) : new Date()));
  const [ficha, setFicha] = useState(null);
  const [cambiarFecha, setCambiarFecha] = useState(null);
  const [pendCita, setPendCita] = useState(null);
  const [filtroPeticion, setFiltroPeticion] = useState(ctx0?.filtroPeticion || "todas");
  const [filtroMaterial, setFiltroMaterial] = useState(ctx0?.filtroMaterial || "todos");
  // Selección para ruta (persistente en sessionStorage mientras se navega por el calendario / Reparto)
  const [selActivo, setSelActivo] = useState(!!ctx0?.selActivo);
  const [selItems, setSelItems] = useState(() => leerSeleccion());
  const [prepararOpen, setPrepararOpen] = useState(false);
  useEffect(() => { guardarSeleccion(selItems); }, [selItems]);
  // Al volver de Reparto con rutas creadas: las líneas enrutadas salen de la selección
  const enrutadasRef = React.useRef(null);
  useEffect(() => {
    const enrutadas = location.state?.lineasEnrutadas;
    if (enrutadas?.length && enrutadasRef.current !== location.state) {
      enrutadasRef.current = location.state;
      setSelItems((arr) => arr.filter((s) => !enrutadas.includes(s.id))); toast.success(`${enrutadas.length} trabajo(s) ya en ruta: retirados de la selección`);
    }
  }, [location.state]);
  // Tras cada carga: poda de la selección (lo que ya está en ruta / realizado deja de ser elegible)
  useEffect(() => {
    if (!montajes.length || !selItems.length) return;
    const porId = new Map(montajes.map((m) => [m.id, m]));
    const fuera = selItems.filter((s) => porId.has(s.id) && motivoNoElegible(porId.get(s.id)));
    if (fuera.length) { setSelItems((arr) => arr.filter((s) => !fuera.some((f) => f.id === s.id))); toast.info(`${fuera.length} trabajo(s) ya no elegibles (en ruta o realizados) salen de la selección`); }
  }, [montajes]); // eslint-disable-line react-hooks/exhaustive-deps
  const seleccion = useMemo(() => ({
    activo: selActivo, setActivo: setSelActivo, items: selItems, ids: new Set(selItems.map((s) => s.id)),
    toggle: (m) => setSelItems((arr) => arr.some((s) => s.id === m.id) ? arr.filter((s) => s.id !== m.id) : [...arr, snapshotMontaje(m)]),
    limpiar: () => setSelItems([]),
  }), [selActivo, selItems]);
  const irAReparto = (fechaRuta) => {
    const payload = { origen: "calendario", fecha_ruta: fechaRuta, linea_ids: selItems.map((s) => s.id), items: selItems };
    guardarCtx({ view, cursor: format(cursor, "yyyy-MM-dd"), dia: format(dia, "yyyy-MM-dd"), filtroProvincias, filtroPeticion, filtroMaterial, mostrarRealizados, sugerir, selActivo });
    sessionStorage.setItem(KEY_REPARTO_SEL, JSON.stringify(payload));
    setPrepararOpen(false);
    nav("/reparto-inteligente", { state: { seleccion: payload } });
  };

  useEffect(() => { localStorage.setItem("dnf-cal-modo", modo); }, [modo]);
  useEffect(() => { localStorage.setItem("dnf-cal-view", view); }, [view]);

  const load = async () => {
    setLoading(true);
    try {
      if (modo === "rutas") {
        const { data } = await api.get("/rutas");
        setRutas(data);
      } else {
        // Rango amplio: mes actual +/- 1 para tener contexto al navegar
        const desde = format(subMonths(startOfMonth(cursor), 1), "yyyy-MM-dd");
        const hasta = format(addMonths(endOfMonth(cursor), 1), "yyyy-MM-dd");
        const { data } = await api.get("/calendario/montajes", { params: { desde, hasta, incluir_sin_fecha: true, incluir_realizados: mostrarRealizados } });
        setMontajes(data);
        setSugeridos([]);
      }
    } catch (e) {
      toast.error("Error cargando " + (modo === "rutas" ? "rutas" : "montajes"));
    } finally {
      setLoading(false);
    }
  };
  // Recargar cuando cambie modo o cuando cursor cambie de mes (para montajes)
  const cursorMonthKey = format(cursor, "yyyy-MM");
  useEffect(() => { load(); }, [modo, cursorMonthKey, mostrarRealizados]);

  // Rango de fechas según vista
  const { rangeStart, rangeEnd, days } = useMemo(() => {
    if (view === "mes") {
      const ms = startOfMonth(cursor);
      const me = endOfMonth(cursor);
      const s = startOfWeek(ms, { weekStartsOn: 1 });
      const e = endOfWeek(me, { weekStartsOn: 1 });
      return { rangeStart: s, rangeEnd: e, days: eachDayOfInterval({ start: s, end: e }) };
    }
    // semana
    const s = startOfWeek(cursor, { weekStartsOn: 1 });
    const e = endOfWeek(cursor, { weekStartsOn: 1 });
    return { rangeStart: s, rangeEnd: e, days: eachDayOfInterval({ start: s, end: e }) };
  }, [view, cursor]);

  // Filtros de montajes (provincia + campaña + material) — compartidos por Mes/Semana/Lista
  const pasaFiltros = (mo) =>
    (filtroProvincias.length === 0 || filtroProvincias.includes((mo.espacio?.provincia || "").toLowerCase().trim()))
    && (filtroPeticion === "todas" || mo.peticion?.id === filtroPeticion)
    && pasaFiltroMaterial(mo, filtroMaterial);
  const montajesBase = useMemo(() => montajes.filter(pasaFiltros), [montajes, filtroProvincias, filtroPeticion, filtroMaterial]); // eslint-disable-line react-hooks/exhaustive-deps
  // Conjunto ÚNICO que consumen Mes/Semana/Día/Lista/Mapa: filtrados + sugeridos por proximidad (si activo)
  const montajesFiltrados = useMemo(() => {
    if (!sugerir || !sugeridos.length) return montajesBase;
    const ids = new Set(montajesBase.map((m) => m.id));
    return [...montajesBase, ...sugeridos.filter((s) => !ids.has(s.id))];
  }, [montajesBase, sugerir, sugeridos]);
  const toggleProvincia = (p) => setFiltroProvincias((arr) => arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]);
  // «Sin fecha» operativo: sin fecha_programada NI cita (fecha_visible null), pendiente/reprogramable (la API ya excluye realizados/cerradas/canceladas)
  const esSinFecha = (m) => !m.fecha_visible && !m.realizado && m.reprogramable !== false && !m.sugerido;
  const sinFechaTotal = useMemo(() => montajes.filter(esSinFecha).length, [montajes]);
  const sinFechaFiltrados = useMemo(() => montajesBase.filter(esSinFecha).length, [montajesBase]);
  const irASinFecha = () => {
    if (view !== "semana") setView("semana");
    setTimeout(() => {
      const col = document.querySelector('[data-testid="kanban-col-sin-fecha"]');
      if (!col) return;
      col.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      col.classList.add("ring-2", "ring-amber-400");
      setTimeout(() => col.classList.remove("ring-2", "ring-amber-400"), 1800);
    }, view !== "semana" ? 350 : 0);
  };

  // Sugerencias por proximidad: reutiliza el motor de corredor de Reparto F2 vía /calendario/montajes/cercanos (sólo lectura)
  const cargarSugeridos = async () => {
    const ids = montajesBase.filter((m) => !m.realizado && m.espacio?.lat != null).map((m) => m.id).slice(0, 60);
    if (!ids.length) { toast.info("No hay trabajos geolocalizados en el conjunto actual para buscar cercanos"); setSugeridos([]); return; }
    setSugLoading(true);
    try {
      const desde = format(subMonths(startOfMonth(cursor), 1), "yyyy-MM-dd");
      const hasta = format(addMonths(endOfMonth(cursor), 1), "yyyy-MM-dd");
      const { data } = await api.get("/calendario/montajes/cercanos", { params: { desde, hasta, linea_ids: ids.join(",") } });
      setSugeridos(data.sugeridos || []);
      toast.success(`${(data.sugeridos || []).length} trabajo(s) sugerido(s) por proximidad · ${data.metrica === "haversine_x1.3" ? "km estimados (línea recta × 1.3)" : data.metrica}`);
    } catch (err) { toast.error(err?.response?.data?.detail?.msg || err?.response?.data?.detail || "No se pudieron calcular cercanos"); setSugeridos([]); }
    finally { setSugLoading(false); }
  };
  useEffect(() => { if (sugerir && modo === "montajes") cargarSugeridos(); else setSugeridos([]); }, [sugerir, montajesBase.length, filtroProvincias.join("|"), filtroPeticion]); // eslint-disable-line react-hooks/exhaustive-deps
  const peticionesDisponibles = useMemo(() => {
    const m = new Map();
    for (const mo of montajes) if (mo.peticion?.id && !m.has(mo.peticion.id)) m.set(mo.peticion.id, `${mo.peticion.codigo || ""} · ${mo.peticion.nombre || ""}`);
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
  }, [montajes]);

  // Kanban semanal (modo montajes): mover = cambiar fecha_programada REAL de la línea (optimista + rollback)
  const moverMontaje = async (m, nuevaFecha, confirmado = false) => {
    if (!m.reprogramable || m.sugerido) { toast.warning(m.bloqueo_motivo || "Este montaje no se puede reprogramar", { duration: 6000 }); return false; }
    if (m.fecha_cita && nuevaFecha && nuevaFecha !== m.fecha_cita && !confirmado) { setPendCita({ m, fecha: nuevaFecha }); return false; }
    const anterior = m.fecha_programada ? m.fecha_programada.slice(0, 10) : null;
    const prevList = montajes;
    setMontajes(montajes.map(x => x.id === m.id ? { ...x, fecha_programada: nuevaFecha, fecha_visible: nuevaFecha || x.fecha_cita || null, fecha_origen: nuevaFecha ? "fecha_programada" : (x.fecha_cita ? "cita" : null) } : x));
    try {
      await api.patch(`/calendario/montajes/${m.id}/fecha`, { fecha_programada: nuevaFecha, confirmar_cita: confirmado });
      const f = (d) => d ? format(parseISO(d), "EEE d MMM", { locale: es }) : "sin fecha";
      toast.success(`${m.espacio?.nombre || m.peticion?.codigo}: ${f(anterior)} → ${f(nuevaFecha)}${m.fecha_cita && nuevaFecha && nuevaFecha !== m.fecha_cita ? ` · cita ${f(m.fecha_cita)} conservada` : ""}`);
      return true;
    } catch (err) {
      setMontajes(prevList);
      const d = err?.response?.data?.detail;
      toast.error(d?.msg || d || "No se pudo reprogramar", { duration: 7000 });
      if (d?.code === "en_ruta" || d?.code === "no_reprogramable") load();
      return false;
    }
  };

  // Indexar por día (rutas o montajes)
  const itemsPorDia = useMemo(() => {
    const m = {};
    if (modo === "rutas") {
      for (const r of rutas) {
        if (!r.fecha) continue;
        const k = r.fecha.slice(0, 10);
        (m[k] = m[k] || []).push(r);
      }
      Object.values(m).forEach(arr => arr.sort((a, b) => (a.hora_salida || "").localeCompare(b.hora_salida || "")));
    } else {
      for (const l of montajesFiltrados) {
        const fv = l.fecha_visible || l.fecha_programada;   // Mes/Lista: programada o cita (misma semántica que Semana)
        if (!fv) continue;
        const k = fv.slice(0, 10);
        (m[k] = m[k] || []).push(l);
      }
      Object.values(m).forEach(arr => arr.sort(
        (a, b) => (a.espacio?.provincia || "").localeCompare(b.espacio?.provincia || "", "es")
      ));
    }
    return m;
  }, [modo, rutas, montajesFiltrados]);

  // Provincias únicas presentes en los montajes cargados (para filtro)
  const provinciasDisponibles = useMemo(() => {
    if (modo !== "montajes") return [];
    const set = new Set();
    for (const m of montajes) {
      const p = (m.espacio?.provincia || "").trim();
      if (p) set.add(p);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [montajes, modo]);

  const prev = () => setCursor(view === "mes" ? subMonths(cursor, 1) : subWeeks(cursor, 1));
  const next = () => setCursor(view === "mes" ? addMonths(cursor, 1) : addWeeks(cursor, 1));
  const goToday = () => setCursor(new Date());

  const titulo = view === "mes"
    ? format(cursor, "MMMM yyyy", { locale: es })
    : `Semana del ${format(rangeStart, "d MMM", { locale: es })} al ${format(rangeEnd, "d MMM yyyy", { locale: es })}`;

  // Drag & drop (solo modo rutas)
  const onDragStart = (ruta) => (e) => {
    if (modo !== "rutas") { e.preventDefault(); return; }
    setDragging(ruta);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", ruta.id);
  };
  const onDragOver = (e) => { if (modo === "rutas") { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } };
  const onDrop = (day) => async (e) => {
    if (modo !== "rutas") return;
    e.preventDefault();
    if (!dragging) return;
    const newFecha = format(day, "yyyy-MM-dd");
    if (newFecha === dragging.fecha) { setDragging(null); return; }
    const prev = rutas;
    setRutas(rutas.map(r => r.id === dragging.id ? { ...r, fecha: newFecha } : r));
    try {
      await api.patch(`/rutas/${dragging.id}`, { fecha: newFecha });
      toast.success(`${dragging.codigo || "Ruta"} movida a ${format(day, "d MMM", { locale: es })}`);
    } catch (err) {
      setRutas(prev);
      toast.error(err?.response?.data?.detail || "Error al mover");
    }
    setDragging(null);
  };

  const weekdayLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  return (
    <div className="p-6 max-w-[1800px]">
      <PageHeader
        title={modo === "rutas" ? "Calendario de rutas" : "Calendario de montajes"}
        subtitle={modo === "rutas"
          ? "Vista mensual / semanal. Arrastra rutas para reprogramarlas."
          : "Vista por línea de trabajo. Colores por provincia — planifica los montajes antes de crear rutas."}
        action={
          <Button onClick={() => nav(modo === "rutas" ? "/rutas/nueva" : "/rutas")}
            data-testid={modo === "rutas" ? "cal-nueva-ruta-btn" : "cal-ir-rutas-btn"}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {modo === "rutas"
              ? (<><Plus className="w-4 h-4 mr-1" /> Nueva ruta</>)
              : (<><Truck className="w-4 h-4 mr-1" /> Ir a rutas</>)}
          </Button>
        }
      />

      {/* Tab: Rutas / Montajes */}
      <div className="mb-4 flex items-center gap-2 bg-zinc-900/40 border border-zinc-800 rounded-lg p-1 w-fit">
        {MODOS.map(m => {
          const Ic = m.icon;
          const active = modo === m.key;
          return (
            <button key={m.key}
              onClick={() => setModo(m.key)}
              data-testid={`cal-modo-${m.key}-btn`}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                active
                  ? "bg-amber-500 text-zinc-900 font-semibold"
                  : "text-zinc-300 hover:bg-zinc-800"
              }`}>
              <Ic className="w-3.5 h-3.5" />{m.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Button onClick={prev} variant="outline" size="sm" data-testid="cal-prev-btn"
            className="h-8 px-2 border-zinc-800">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button onClick={goToday} variant="outline" size="sm" data-testid="cal-today-btn"
            className="h-8 border-zinc-800">
            Hoy
          </Button>
          <Button onClick={next} variant="outline" size="sm" data-testid="cal-next-btn"
            className="h-8 px-2 border-zinc-800">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="text-xl font-semibold text-zinc-100 ml-2 capitalize">{titulo}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filtro por provincia (solo modo montajes) */}
          {modo === "montajes" && provinciasDisponibles.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap max-w-[520px]" data-testid="cal-filtro-provincia">
              <button type="button" onClick={() => setFiltroProvincias([])} data-testid="cal-prov-todas"
                className={`h-7 px-2 text-[11px] rounded border ${filtroProvincias.length === 0 ? "bg-amber-500 text-zinc-900 border-amber-500 font-semibold" : "bg-zinc-900/60 border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>Todas ({montajes.length})</button>
              {provinciasDisponibles.map(p => {
                const k = p.toLowerCase(); const on = filtroProvincias.includes(k);
                return <button key={p} type="button" onClick={() => toggleProvincia(k)} data-testid={`cal-prov-${k.replace(/\s+/g, "-")}`}
                  className={`h-7 px-2 text-[11px] rounded border ${on ? "bg-amber-500/20 border-amber-400 text-amber-100 font-semibold" : "bg-zinc-900/60 border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>{on ? "✓ " : ""}{p}</button>;
              })}
            </div>
          )}
          {modo === "montajes" && VIEWS_SELECCION.includes(view) && (
            <button type="button" onClick={() => setSelActivo((v) => !v)} data-testid="cal-seleccionar-ruta-btn" title="Marca varios trabajos del día/semana y prepara una ruta con ellos (Reparto Inteligente)"
              className={`h-8 px-2 text-[11px] rounded border inline-flex items-center gap-1 ${selActivo ? "bg-amber-500 text-zinc-900 border-amber-500 font-semibold" : "bg-zinc-900/60 border-amber-500/40 text-amber-200 hover:border-amber-400"}`}>
              <ListChecks className="w-3.5 h-3.5" /> {selActivo ? "Seleccionando…" : "Seleccionar para ruta"}
            </button>
          )}
          {modo === "montajes" && sinFechaTotal > 0 && (
            <button type="button" onClick={irASinFecha} data-testid="cal-sin-fecha-btn" data-count={sinFechaTotal}
              title={`Trabajos pendientes sin fecha de montaje ni cita${sinFechaFiltrados !== sinFechaTotal ? ` · ${sinFechaFiltrados} visibles con los filtros actuales` : ""} · clic: ir a la columna «Sin fecha» (Semana)`}
              className="h-8 px-2 text-[11px] rounded border border-orange-500/50 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20 inline-flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" /> <b>{sinFechaTotal}</b> sin fecha{sinFechaFiltrados !== sinFechaTotal && <span className="text-orange-300/70">({sinFechaFiltrados} filtr.)</span>}
            </button>
          )}
          {modo === "montajes" && (
            <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-300 cursor-pointer h-8 px-2 bg-zinc-900/60 border border-zinc-800 rounded" title="Incluir trabajo ya ejecutado (atenuado, no reprogramable)">
              <input type="checkbox" checked={mostrarRealizados} onChange={(e) => setMostrarRealizados(e.target.checked)} data-testid="cal-mostrar-realizados" /> Mostrar realizados
            </label>
          )}
          {modo === "montajes" && (
            <label className="inline-flex items-center gap-1.5 text-[11px] text-amber-200 cursor-pointer h-8 px-2 bg-zinc-900/60 border border-amber-500/30 rounded" title="Trabajos pendientes cercanos al corredor del conjunto visible (motor de Reparto Inteligente). Sólo sugerencia: no cambia nada.">
              <input type="checkbox" checked={sugerir} onChange={(e) => setSugerir(e.target.checked)} data-testid="cal-sugerir-cercanos" /> <SparklesIcon className="w-3 h-3" /> Sugerir zonas cercanas{sugLoading ? "…" : sugerir && sugeridos.length ? ` (${sugeridos.length})` : ""}
            </label>
          )}
          {modo === "montajes" && peticionesDisponibles.length > 0 && (
            <select value={filtroPeticion} onChange={(e) => setFiltroPeticion(e.target.value)} data-testid="cal-filtro-peticion"
              className="h-8 text-xs bg-zinc-900/60 border border-zinc-800 text-zinc-200 rounded px-2 max-w-[220px]">
              <option value="todas">Todas las campañas</option>
              {peticionesDisponibles.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          )}
          {modo === "montajes" && (
            <select value={filtroMaterial} onChange={(e) => setFiltroMaterial(e.target.value)} data-testid="cal-filtro-material"
              className="h-8 text-xs bg-zinc-900/60 border border-zinc-800 text-zinc-200 rounded px-2">
              <option value="todos">Material: todos</option>
              <option value="listo">Material listo (completo o parcial)</option>
              <option value="completo">Solo completo</option>
              <option value="almacen">En almacén</option>
              <option value="pdv">En punto de venta</option>
              <option value="parcial">Parcial</option>
              <option value="no_listo">Material pendiente</option>
            </select>
          )}
          <div className="flex items-center gap-1 bg-zinc-900/60 border border-zinc-800 rounded-lg p-1">
            {(modo === "montajes" ? VIEWS : VIEWS_RUTAS).map(v => (
              <button key={v} onClick={() => setView(v)}
                data-testid={`cal-view-${v}-btn`}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  view === v ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-300 hover:bg-zinc-800"
                }`}>
                {v === "mes" && <><CalendarDays className="w-3 h-3 inline mr-1" />Mes</>}
                {v === "semana" && <><CalendarDays className="w-3 h-3 inline mr-1" />Semana</>}
                {v === "dia" && <><EyeIcon className="w-3 h-3 inline mr-1" />Día</>}
                {v === "lista" && <><List className="w-3 h-3 inline mr-1" />Lista</>}
                {v === "mapa" && <><MapPinIcon className="w-3 h-3 inline mr-1" />Mapa</>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {modo === "montajes" && <LeyendaMaterial className="px-1" />}
      {modo === "montajes" && <BarraSeleccion seleccion={seleccion} onPreparar={() => setPrepararOpen(true)} />}
      {modo === "montajes" && <PrepararRutaDialog open={prepararOpen} onOpenChange={setPrepararOpen} items={selItems} fechaSugerida={view === "dia" ? format(dia, "yyyy-MM-dd") : ""} onIr={irAReparto} />}

      {loading && (
        <div className="text-center py-12 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin inline" />
        </div>
      )}

      {!loading && view === "mes" && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid="cal-mes-grid">
          {/* Header dias */}
          <div className="grid grid-cols-7 bg-zinc-950/60 border-b border-zinc-800">
            {weekdayLabels.map(w => (
              <div key={w} className="px-2 py-2 text-[11px] uppercase tracking-wider text-zinc-500 text-center">
                {w}
              </div>
            ))}
          </div>
          {/* Grid */}
          <div className="grid grid-cols-7 auto-rows-fr">
            {days.map((day, i) => {
              const k = format(day, "yyyy-MM-dd");
              const inMonth = isSameMonth(day, cursor);
              const today = isToday(day);
              const itemsDia = itemsPorDia[k] || [];
              return (
                <div
                  key={i}
                  onDragOver={onDragOver}
                  onDrop={onDrop(day)}
                  data-testid={`cal-day-${k}`}
                  className={`min-h-[110px] border-r border-b border-zinc-800/60 p-1.5 ${
                    inMonth ? "bg-zinc-950/40" : "bg-zinc-950/10"
                  } ${dragging ? "hover:bg-amber-500/5" : ""}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs ${today
                      ? "bg-amber-500 text-zinc-900 font-bold w-5 h-5 rounded-full flex items-center justify-center"
                      : inMonth ? "text-zinc-300" : "text-zinc-600"}`}>
                      {format(day, "d")}
                    </span>
                    {itemsDia.length > 0 && (
                      <span className="text-[10px] text-zinc-500">{itemsDia.length}</span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {itemsDia.slice(0, 4).map(item => (
                      modo === "rutas"
                        ? <RutaPill key={item.id} ruta={item} onClick={() => nav(`/rutas/${item.id}`)}
                            onDragStart={onDragStart(item)} compact />
                        : <MontajePill key={item.id} montaje={item}
                            onClick={() => nav(`/peticiones/${item.peticion.id}`)} compact />
                    ))}
                    {itemsDia.length > 4 && (
                      <div className="text-[10px] text-zinc-500 pl-1">+{itemsDia.length - 4} más</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && view === "semana" && modo === "montajes" && (
        <div data-testid="cal-semana-kanban">
          <div className="text-[11px] text-zinc-500 mb-2">Planificador semanal · arrastra una tarjeta a otro día (o a «Sin fecha») para cambiar la <b>fecha de montaje</b> de la línea. Las tarjetas 🔒 pertenecen a una ruta: cambia la fecha desde la ruta o retíralas primero.</div>
          <div className="mb-2"><ResumenConjunto items={montajesFiltrados.filter((m) => { const fv = (m.fecha_visible || "").slice(0, 10); return fv && days.some((d) => format(d, "yyyy-MM-dd") === fv); })} testId="cal-semana-resumen" /></div>
          <KanbanMontajes days={days} montajes={montajesFiltrados} provinciaColor={provinciaColor}
            onCardClick={(m) => nav(`/peticiones/${m.peticion.id}`)} onMover={moverMontaje} onVer={setFicha} onCambiarFecha={setCambiarFecha} seleccion={seleccion} />
        </div>
      )}
      {!loading && modo === "montajes" && view === "dia" && (
        <DiaMontajes dia={dia} setDia={setDia} items={montajesFiltrados} provinciaColor={provinciaColor} onVer={setFicha} onCambiarFecha={setCambiarFecha} onCardClick={(m) => nav(`/peticiones/${m.peticion.id}`)} seleccion={seleccion} />
      )}
      {!loading && modo === "montajes" && view === "mapa" && <MapaMontajes items={montajesFiltrados} onVer={setFicha} seleccion={seleccion} />}
      {modo === "montajes" && (
        <>
          <FichaMontaje m={ficha} open={!!ficha} onOpenChange={(o) => !o && setFicha(null)} provinciaColor={provinciaColor} onCambiarFecha={(m) => { setCambiarFecha(m); }} />
          <CambiarFechaDialog m={cambiarFecha} open={!!cambiarFecha} onOpenChange={(o) => !o && setCambiarFecha(null)} onMover={moverMontaje} />
          <ConfirmarCitaDialog pend={pendCita} onCancel={() => setPendCita(null)} onConfirm={async () => { const p = pendCita; setPendCita(null); const ok = await moverMontaje(p.m, p.fecha, true); if (ok) { setCambiarFecha(null); } }} />
        </>
      )}

      {!loading && view === "semana" && modo === "rutas" && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid="cal-semana-grid">
          <div className="grid grid-cols-7 divide-x divide-zinc-800/60">
            {days.map((day, i) => {
              const k = format(day, "yyyy-MM-dd");
              const today = isToday(day);
              const itemsDia = itemsPorDia[k] || [];
              return (
                <div key={i}
                  onDragOver={onDragOver}
                  onDrop={onDrop(day)}
                  data-testid={`cal-week-day-${k}`}
                  className={`min-h-[380px] p-2 ${today ? "bg-amber-500/5" : ""}`}>
                  <div className={`text-center pb-2 mb-2 border-b ${today ? "border-amber-500/40" : "border-zinc-800"}`}>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500">{weekdayLabels[i]}</div>
                    <div className={`text-xl font-bold ${today ? "text-amber-300" : "text-zinc-200"}`}>{format(day, "d")}</div>
                    <div className="text-[10px] text-zinc-600">{format(day, "MMM", { locale: es })}</div>
                  </div>
                  <div className="space-y-1.5">
                    {itemsDia.length === 0 && (
                      <div className="text-[10px] text-zinc-700 italic text-center pt-4">
                        {modo === "rutas" ? "Sin rutas" : "Sin montajes"}
                      </div>
                    )}
                    {itemsDia.map(item => (
                      modo === "rutas"
                        ? <RutaPill key={item.id} ruta={item} onClick={() => nav(`/rutas/${item.id}`)}
                            onDragStart={onDragStart(item)} />
                        : <MontajePill key={item.id} montaje={item}
                            onClick={() => nav(`/peticiones/${item.peticion.id}`)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vista lista — agrupada por fecha */}
      {!loading && view === "lista" && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl divide-y divide-zinc-800/60"
             data-testid="cal-lista-view">
          {Object.keys(itemsPorDia).sort().length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">
              No hay {modo === "rutas" ? "rutas" : "montajes"} en el rango visible.
            </div>
          )}
          {Object.keys(itemsPorDia).sort().map(k => {
            const items = itemsPorDia[k];
            const d = parseISO(k);
            return (
              <div key={k} className="px-4 py-3" data-testid={`cal-lista-dia-${k}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-sm font-semibold text-amber-300 capitalize">
                    {format(d, "EEEE d MMM yyyy", { locale: es })}
                  </div>
                  <span className="text-[10px] text-zinc-500 bg-zinc-900 border border-zinc-800 px-1.5 py-0.5 rounded">
                    {items.length} {modo === "rutas" ? "ruta(s)" : "montaje(s)"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {items.map(item => (
                    modo === "rutas"
                      ? <RutaPill key={item.id} ruta={item} onClick={() => nav(`/rutas/${item.id}`)}
                          onDragStart={onDragStart(item)} />
                      : <MontajePill key={item.id} montaje={item}
                          onClick={() => nav(`/peticiones/${item.peticion.id}`)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Leyenda dinámica según modo */}
      {modo === "rutas" ? (
        <div className="flex items-center gap-3 mt-4 text-[11px] text-zinc-500 flex-wrap">
          <span>Estados:</span>
          {Object.entries(ESTADO_COLOR).map(([k, c]) => (
            <span key={k} className={`px-2 py-0.5 rounded border ${c} capitalize`}>{k.replace("_", " ")}</span>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-4 text-[11px] text-zinc-500 flex-wrap">
          <span>Provincia:</span>
          {[
            ["Cádiz",    PROVINCIA_COLOR.cadiz],
            ["Sevilla",  PROVINCIA_COLOR.sevilla],
            ["Huelva",   PROVINCIA_COLOR.huelva],
            ["Málaga",   PROVINCIA_COLOR.malaga],
            ["Portugal", PROVINCIA_COLOR.portugal],
            ["Córdoba",  PROVINCIA_COLOR.cordoba],
            ["Granada",  PROVINCIA_COLOR.granada],
            ["Almería",  PROVINCIA_COLOR.almeria],
            ["Jaén",     PROVINCIA_COLOR.jaen],
            ["Otra / Sin espacio", PROVINCIA_DEFAULT_COLOR],
          ].map(([name, cls]) => (
            <span key={name} className={`px-2 py-0.5 rounded border ${cls}`}>{name}</span>
          ))}
        </div>
      )}
    </div>
  );
}
